import React from "react";
import { ContentBlock } from "@/types/case-study";
import { CircleAlert, TriangleAlert, Lightbulb, CircleCheck, CircleX, GripVertical, Minus } from "lucide-react";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { cleanWavyUnderlines } from "@/utils/cleanWavyUnderlines";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface CalloutBlockProps {
  block: ContentBlock;
  onUpdate: (content: any) => void;
  onRemove?: () => void;
  isActive?: boolean;
  isRemovable?: boolean;
}

type CalloutVariant = "note" | "info" | "warning" | "tip" | "check" | "danger";

// Map variants to icons
const CALLOUT_ICONS: Record<CalloutVariant, React.ComponentType<{ className?: string }>> = {
  note: CircleAlert,
  info: Lightbulb,
  warning: TriangleAlert,
  tip: CircleAlert,
  check: CircleCheck,
  danger: CircleX,
};

// Map variants to Alert component variants
const CALLOUT_ALERT_VARIANTS: Record<CalloutVariant, "default" | "destructive" | "warning" | "success" | "info" | "tip"> = {
  note: "default",
  info: "info",
  warning: "warning",
  tip: "tip",
  check: "success",
  danger: "destructive",
};

// Variant labels for the dropdown
const CALLOUT_VARIANT_LABELS: Record<CalloutVariant, string> = {
  note: "Note",
  info: "Info",
  warning: "Warning",
  tip: "Tip",
  check: "Check",
  danger: "Danger",
};

export const CalloutBlock: React.FC<CalloutBlockProps> = ({
  block,
  onUpdate,
  onRemove,
  isActive = false,
  isRemovable = true,
}) => {
  const handleTextChange = (text: string) => {
    onUpdate({
      ...block.content,
      text,
    });
  };

  const handleVariantChange = (variant: string) => {
    onUpdate({
      ...block.content,
      variant,
    });
  };

  const text = block.content?.text || "";
  const variant = (block.content?.variant || "info") as CalloutVariant;

  const IconComponent = CALLOUT_ICONS[variant];
  const alertVariant = CALLOUT_ALERT_VARIANTS[variant];

  return (
    <div className={`group relative ${isActive ? '' : ''}`}>
      {isActive ? (
        // Edit Mode - Card with editor
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="p-4 space-y-4">
            {/* Variant Selector */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-900">
                Callout Type
              </label>
              <Select value={variant} onValueChange={handleVariantChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select callout type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CALLOUT_VARIANT_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Preview */}
              <div className="pt-2">
                <Alert variant={alertVariant}>
                  <IconComponent className="h-4 w-4" />
                  <AlertDescription>
                    {text ? (
                      <div dangerouslySetInnerHTML={{ __html: cleanWavyUnderlines(text) }} />
                    ) : (
                      <span className="text-gray-400">Enter your callout content to see a preview...</span>
                    )}
                  </AlertDescription>
                </Alert>
              </div>
            </div>

            {/* Callout Text Editor */}
            <div className="">
              <div className="min-h-[160px] overflow-hidden">
                <RichTextEditor
                  value={text}
                  onChange={handleTextChange}
                  placeholder="Add your callout content here..."
                  maxLength={2000}
                />
              </div>
            </div>
          </div>

          {isRemovable && onRemove && (
            <button
              onClick={onRemove}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-70 hover:opacity-100 transition-opacity hover:bg-red-600"
              title="Delete this content block"
            >
              ×
            </button>
          )}
        </div>
      ) : (
        // Preview Mode - Using Alert component
        <div className="py-2">
          <Alert variant={alertVariant}>
            <IconComponent className="h-4 w-4" />
            <AlertDescription>
              {text ? (
                <div dangerouslySetInnerHTML={{ __html: cleanWavyUnderlines(text) }} />
              ) : (
                <span className="text-gray-400">Enter your callout content...</span>
              )}
            </AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  );
};
